import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '@/components/base/ProductCard';
import type { Product } from '@/types';

interface CategorySectionProps {
  categoryId: string;
  categoryName: string;
  products: Product[];
  totalProducts: number;
  maxVisible?: number;
}

const CATEGORY_ICONS: Record<string, string> = {
  solar: 'ri-sun-line',
  'materia-prima': 'ri-box-3-line',
  herramientas: 'ri-hammer-line',
  baterias: 'ri-battery-charge-line',
  inversores: 'ri-flashlight-line',
  cables: 'ri-plug-line',
  accesorios: 'ri-settings-3-line',
  iluminacion: 'ri-lightbulb-line',
  electrodomesticos: 'ri-tv-line',
  climatizacion: 'ri-temp-hot-line',
  seguridad: 'ri-shield-line',
  kits: 'ri-tools-line',
  paneles: 'ri-sun-line',
};

function getCategoryIcon(categoryId: string): string {
  const key = Object.keys(CATEGORY_ICONS).find(
    (k) => categoryId.includes(k) || k.includes(categoryId),
  );
  return CATEGORY_ICONS[key ?? ''] ?? 'ri-grid-line';
}

const CARD_W = 220;
const CARD_GAP = 14;

export default function CategorySection({
  categoryId,
  categoryName,
  products,
  totalProducts,
  maxVisible = 10,
}: CategorySectionProps) {
  const visibleProducts = products.slice(0, maxVisible);
  const hasMore = totalProducts > maxVisible;
  const icon = getCategoryIcon(categoryId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'right' ? (CARD_W + CARD_GAP) * 3 : -(CARD_W + CARD_GAP) * 3, behavior: 'smooth' });
  };

  return (
    <div className="mb-14">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 px-4 md:px-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0" style={{ background: '#e8f8fd' }}>
            <i className={`${icon} text-base`} style={{ color: '#4bbfdf' }} />
          </div>
          <div>
            <h2 className="font-bold text-base md:text-lg text-stone-900 leading-tight">
              {categoryName}
            </h2>
            <p className="text-xs text-stone-400 mt-0.5">
              {totalProducts} {totalProducts === 1 ? 'producto' : 'productos'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Scroll arrows — solo en desktop */}
          <button
            onClick={() => scroll('left')}
            aria-label="Desplazar izquierda"
            className={`hidden md:flex w-8 h-8 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-400 transition-all duration-200 cursor-pointer ${canScrollLeft ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}
            style={{} as React.CSSProperties}
            onMouseEnter={(e) => { const el = e.currentTarget; el.style.background = '#4bbfdf'; el.style.color = '#fff'; el.style.borderColor = '#4bbfdf'; }}
            onMouseLeave={(e) => { const el = e.currentTarget; el.style.background = ''; el.style.color = ''; el.style.borderColor = ''; }}
          >
            <i className="ri-arrow-left-s-line text-base" />
          </button>
          <button
            onClick={() => scroll('right')}
            aria-label="Desplazar derecha"
            className={`hidden md:flex w-8 h-8 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-400 transition-all duration-200 cursor-pointer ${canScrollRight ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}
            onMouseEnter={(e) => { const el = e.currentTarget; el.style.background = '#4bbfdf'; el.style.color = '#fff'; el.style.borderColor = '#4bbfdf'; }}
            onMouseLeave={(e) => { const el = e.currentTarget; el.style.background = ''; el.style.color = ''; el.style.borderColor = ''; }}
          >
            <i className="ri-arrow-right-s-line text-base" />
          </button>

          {hasMore && (
            <Link
              to={`/category/${categoryId}`}
              className="inline-flex items-center gap-1 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ml-1"
              style={{ color: '#4bbfdf' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#2aa8cc'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#4bbfdf'; }}
            >
              Ver todos
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          )}
        </div>
      </div>

      {/* ── MOBILE: AliExpress grid 2 columnas ── */}
      <div className="md:hidden px-4">
        <div className="grid grid-cols-2 gap-2.5">
          {visibleProducts.map((product) => (
            <ProductCard key={product.id} product={product} variant="aliexpress" />
          ))}
        </div>

        {hasMore && (
          <Link
            to={`/category/${categoryId}`}
            className="mt-3 flex items-center justify-center gap-2 w-full py-3 rounded-xl border border-stone-200 bg-white text-sm font-semibold cursor-pointer active:bg-stone-50 transition-colors"
              style={{ color: '#4bbfdf' }}
          >
            Ver todos los productos
            <i className="ri-arrow-right-line text-sm" />
          </Link>
        )}
      </div>

      {/* ── DESKTOP: scroll horizontal clásico ── */}
      <div className="hidden md:block relative">
        <div
          ref={scrollRef}
          onScroll={updateScrollState}
          className="flex gap-3.5 overflow-x-auto pb-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {visibleProducts.map((product) => (
            <div
              key={product.id}
              className="shrink-0"
              style={{ width: `${CARD_W}px` }}
            >
              <ProductCard product={product} />
            </div>
          ))}

          {hasMore && (
            <div className="shrink-0 flex items-center justify-center" style={{ width: `${CARD_W}px` }}>
              <Link
                to={`/category/${categoryId}`}
                className="flex flex-col items-center justify-center gap-3 w-full h-full min-h-[280px] rounded-2xl border-2 border-dashed border-stone-200 bg-stone-50 transition-all duration-200 cursor-pointer group"
                style={{} as React.CSSProperties}
                onMouseEnter={(e) => { const el = e.currentTarget; el.style.borderColor = '#4bbfdf'; el.style.background = '#e8f8fd'; }}
                onMouseLeave={(e) => { const el = e.currentTarget; el.style.borderColor = ''; el.style.background = ''; }}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-stone-200 transition-all duration-200"
                  style={{} as React.CSSProperties}
                >
                  <i className="ri-arrow-right-line text-stone-400" />
                </div>
                <div className="text-center px-4">
                  <p className="text-sm font-semibold text-stone-700">Ver todos</p>
                  <p className="text-xs text-stone-400 mt-0.5">+{totalProducts - maxVisible} más</p>
                </div>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="mt-10 border-b border-stone-100" />
    </div>
  );
}


